/**
 * Right-side detail panel for ONE Meta ad (#5981) — the Meta sibling of Google's per-ad page at
 * ``routes/.../google/$campaignslug/_slug/ad/$adId.tsx``.
 *
 * Google's ad page carries keyword/negative/label tabs that have no Meta analog; the Meta ad is
 * one creative, so this page is a single Overview: the ad's info form (Google's grid layout) and
 * the same editable copy card the campaign's Assets tab uses (inline edit + Regenerate).
 */

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { AlertCircle, ArrowRight, Eye, Loader2, Pause } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Spinner } from '@/components/glitch-ui/spinner';
import { SaveChangesButton } from '@/components/glitch-ui/save-changes-button';
import { ConformationModal } from '@/components/glitch-ui/confirmation-modal';
import { MetaAdActionsDropdown } from '@/components/campaign/meta/meta-actions-dropdown';
import { MetaAdPreviewModal } from '@/components/campaign/meta/meta-ad-preview-modal';
import { tabButtonClass } from '@/components/glitch-ui/tab-card-panel';
import { CampaignInfoForm, type CampaignInfoRow } from '@/components/campaign/detail/info-form';
import { MetaAdCard, CreativeThumbnail, useMetaAdCopyEditor } from '@/components/campaign/meta/meta-campaign-detail-panel';
import { useMetaLaunchControl, useMetaPauseControl } from '@/components/campaign/meta/meta-detail-adapter';
import { useUnsavedChangesWarning } from '@/hooks/useUnsavedChangesWarning';
import {
  creativeAssetQueryOptions,
  metaCampaignDetailQueryOptions,
  metaCampaignProgressQueryOptions,
  metaCampaignTaskStatusQueryOptions,
  metaCreativePerformanceQueryOptions,
  metaIntegrationStatusQueryOptions,
  useDeleteMetaAdMutation,
  useUpdateMetaAdStatusMutation,
} from '@/lib/query-options';
import { useToast } from '@/hooks/useToast';
import { useCurrencySymbol } from '@/hooks/useCurrencySymbol';
import { usePlatformConnections } from '@/hooks/usePlatformConnections';
import type { MetaAdResponse, MetaAdSetResponse } from '@/lib/types';

/** Google's Overview grid, with the ad's fields: name first, then the descriptive trio. */
function metaAdInfoFields(ad: MetaAdResponse, adSet: MetaAdSetResponse): CampaignInfoRow[] {
  const landingPage = (() => {
    try {
      const url = new URL(ad.destination_url);
      return `${url.origin}${url.pathname === '/' ? '' : url.pathname}`;
    } catch {
      return ad.destination_url;
    }
  })();
  return [
    { label: 'Meta Ad Name', value: ad.name },
    [
      { label: 'Ad Set', value: adSet.name },
      { label: 'Status', value: ad.status },
    ],
    { label: 'Landing Page URL', value: landingPage },
  ];
}

/** The ad's creative in the Visuals column: the letterboxed thumbnail plus its metadata (name,
 *  dimensions, type, size) — the same fields /assets surfaces (#6177). Exported for the ad-set
 *  surface's Creatives tab (#6210), which renders one per angle. */
export function MetaAdVisual({ assetId, alt }: { assetId: number; alt: string }) {
  const { data: asset } = useQuery(creativeAssetQueryOptions(assetId));
  const details: { label: string; value: string }[] = [];
  if (asset) {
    if (asset.original_filename) details.push({ label: 'Name', value: asset.original_filename });
    if (asset.width_px && asset.height_px) {
      details.push({ label: 'Dimensions', value: `${asset.width_px} × ${asset.height_px} px` });
    }
    details.push({ label: 'Type', value: asset.mime_type.replace('image/', '').toUpperCase() });
    details.push({
      label: 'Size',
      value:
        asset.byte_size < 1024 * 1024
          ? `${Math.round(asset.byte_size / 1024)} KB`
          : `${(asset.byte_size / (1024 * 1024)).toFixed(1)} MB`,
    });
  }
  return (
    <div className="overflow-hidden rounded-md border border-gray-200">
      <CreativeThumbnail assetId={assetId} alt={alt} />
      {details.length > 0 && (
        <dl className="space-y-1.5 border-t border-gray-200 p-3 text-sm">
          {details.map((row) => (
            <div key={row.label} className="flex items-start justify-between gap-3">
              <dt className="flex-shrink-0 text-gray-500">{row.label}</dt>
              <dd className="min-w-0 truncate text-right text-gray-900" title={row.value}>
                {row.value}
              </dd>
            </div>
          ))}
        </dl>
      )}
    </div>
  );
}

export function MetaAdDetailPanel({ slug, adId }: { slug: string; adId: number }) {
  const { data, isLoading, isError } = useQuery(metaCampaignDetailQueryOptions(slug));
  const { data: metaStatus } = useQuery(metaIntegrationStatusQueryOptions());
  const { symbol: currencySymbol } = useCurrencySymbol(undefined, metaStatus?.selected_ad_account?.currency);
  const { data: creativePerf } = useQuery(metaCreativePerformanceQueryOptions());

  const adSet = data?.ad_sets.find((candidate) => candidate.ads.some((ad) => ad.id === adId));
  const ad = adSet?.ads.find((candidate) => candidate.id === adId);
  const metric = ad ? creativePerf?.rows.find((row) => row.meta_ad_id === ad.meta_ad_id) : undefined;
  // Copy edits stage in this hook; Save Changes / Cancel live in the toolbar below, the standard
  // top-of-page placement rather than inline in the copy card (#6177).
  const copyEditor = useMetaAdCopyEditor(ad, slug);
  // The editor resyncs on ad.id, so navigating to another ad (or anywhere else) used to discard a
  // dirty draft silently — the shared unsaved-changes modal now intercepts it (#6179).
  const { UnsavedChangesModal } = useUnsavedChangesWarning({
    isDirty: copyEditor.isDirty,
    // The modal navigates once onSave resolves, so a save that didn't persist (validation marks or
    // a server error, both handled inside handleSave) must throw to keep the user on the page.
    onSave: async () => {
      if (!(await copyEditor.handleSave())) {
        throw new Error('The copy was not saved. Fix the marked fields or discard the changes.');
      }
    },
    onDiscard: copyEditor.resetDraft,
    isSaving: copyEditor.isSaving,
    contextName: ad?.name,
  });

  // Go Live / Pause act on the whole campaign — the same controls as the campaign detail toolbar,
  // surfaced here so an ad can be launched in place (#6177). Readiness (ad sets, ads, budget on the
  // right level) is gated inside useMetaLaunchControl.
  const { metaConnected, isLoading: isLoadingConnections } = usePlatformConnections();
  const isMetaDisconnected = !isLoadingConnections && !metaConnected;
  const offerUrl = data?.offer_url ?? null;
  const taskStatus = useQuery(metaCampaignTaskStatusQueryOptions(offerUrl));
  const progress = useQuery(metaCampaignProgressQueryOptions(slug));
  // Both signals, same rationale as the campaign panel (#6215): offer-keyed and campaign-keyed
  // caches can briefly disagree during a retry — either IN_PROGRESS means generating.
  const isGenerating = taskStatus.data?.state === 'IN_PROGRESS' || progress.data?.state === 'IN_PROGRESS';
  const launchControl = useMetaLaunchControl(data, isGenerating);
  const { isPausing, pause } = useMetaPauseControl(data);
  const showLaunchActions = !!data && !isGenerating && !isMetaDisconnected && taskStatus.data?.state !== 'FAILED';

  // Per-ad pause/resume + soft delete (#6179) — Google's per-ad state controls, ad-scoped where the
  // buttons above act on the whole campaign. Withheld mid-generation and while disconnected, same
  // rules as the campaign page's delete.
  const navigate = useNavigate();
  const { toast } = useToast();
  const adStatusMutation = useUpdateMetaAdStatusMutation(slug);
  const deleteAdMutation = useDeleteMetaAdMutation(slug);
  const [showDeleteAdConfirm, setShowDeleteAdConfirm] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const canManageAd = !!ad && !isGenerating && !isMetaDisconnected;
  const isAdActive = ad?.status === 'ACTIVE';
  const toggleAdStatus = async () => {
    if (!ad) return;
    try {
      await adStatusMutation.mutateAsync({ adId: ad.id, status: isAdActive ? 'PAUSED' : 'ACTIVE' });
    } catch (error) {
      toast({
        title: isAdActive ? 'Could Not Pause Ad' : 'Could Not Resume Ad',
        description: error instanceof Error ? error.message : 'Please try again.',
        variant: 'destructive',
      });
    }
  };
  const handleConfirmDeleteAd = async () => {
    if (!ad) return;
    setShowDeleteAdConfirm(false);
    try {
      await deleteAdMutation.mutateAsync(ad.id);
      // Reset the copy draft BEFORE navigating so the unsaved-changes blocker can't intercept the
      // exit of an ad that no longer exists.
      copyEditor.resetDraft();
      navigate({ to: '/campaigns/meta/$metaCampaignSlug', params: { metaCampaignSlug: slug }, replace: true });
    } catch (error) {
      toast({
        title: 'Could Not Delete Ad',
        description: error instanceof Error ? error.message : 'Please try again.',
        variant: 'destructive',
      });
    }
  };

  return (
    <div>
      <div className="flex items-end justify-between">
        <div className="flex items-end gap-1">
          {/* Single-tab page: a static label styled as the active tab, not a button — there is
              nothing to switch to, so a clickable no-op would be misleading (#6163 P3, #6179). */}
          <span className={tabButtonClass(true)}>Overview</span>
        </div>
        {(showLaunchActions || copyEditor.isDirty || canManageAd) && (
          <div className="flex items-center gap-2 pb-1">
            {copyEditor.isDirty && (
              <SaveChangesButton
                className="h-9"
                onClick={copyEditor.handleSave}
                saving={copyEditor.isSaving}
                disabled={copyEditor.isBusy}
              />
            )}
            {showLaunchActions && <launchControl.PaymentGate />}
            {showLaunchActions && data && (data.status === 'ACTIVE' ? (
              <Button variant="outline" className="h-9 text-gray-900" onClick={pause} disabled={isPausing}>
                {isPausing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Pause className="h-4 w-4 mr-2" />}
                Pause Campaign
              </Button>
            ) : (
              <div className="flex flex-col items-start gap-1">
                <Button
                  className="h-9 bg-green-600 hover:bg-green-700 text-white shadow-lg animate-bounce-in-right"
                  onClick={launchControl.launch}
                  disabled={!launchControl.canLaunch}
                >
                  {launchControl.isLaunching ? (
                    <>
                      <Spinner size="sm" className="mr-2" />
                      Going Live...
                    </>
                  ) : (
                    <>
                      Go Live
                      <ArrowRight className="h-4 w-4 ml-1" />
                    </>
                  )}
                </Button>
                {launchControl.unmetChecks.length > 0 && (
                  <p className="text-[11px] leading-snug text-amber-700">
                    Before going live: {launchControl.unmetChecks.join(' · ')}
                  </p>
                )}
              </div>
            ))}
            {/* The per-ad actions (state toggle + soft delete, #6179) live in the Ad dropdown —
                Google's ad-page placement (#6190). Items are ad-scoped where the buttons beside
                the trigger act on the whole campaign; delete confirms in the modal below. */}
            {/* Meta's own rendering of this ad (#6213) — available whenever the ad exists on Meta. */}
            {ad?.meta_ad_id && (
              <Button variant="outline" className="h-9 text-gray-900" onClick={() => setShowPreview(true)}>
                <Eye className="mr-1 h-4 w-4" />
                Preview
              </Button>
            )}
            {canManageAd && (
              <MetaAdActionsDropdown
                canToggleStatus={data?.first_activated_at != null}
                isAdActive={isAdActive}
                onToggleStatus={toggleAdStatus}
                isToggling={adStatusMutation.isPending}
                onDelete={() => setShowDeleteAdConfirm(true)}
                isDeleting={deleteAdMutation.isPending}
              />
            )}
          </div>
        )}
      </div>
      <Card className="rounded-lg rounded-tl-none border bg-white">
        <CardContent className="p-6">
          {isLoading ? (
            <div className="flex items-center gap-2 py-2 text-sm text-gray-500">
              <Spinner className="w-4 h-4" /> Loading Meta ad…
            </div>
          ) : isError || !data || !ad || !adSet ? (
            <div className="flex items-start gap-3 rounded-lg border-l-4 border-[#FCA5A5] bg-[#FEE2E2] p-4 text-[#B91C1C]">
              <AlertCircle className="w-5 h-5 mt-0.5" />
              <div>
                <p className="text-sm font-medium">Could not load this Meta ad.</p>
                <p className="text-xs">It may have been removed. Go back to the campaign and refresh.</p>
              </div>
            </div>
          ) : (
            <div className="space-y-6">
              <CampaignInfoForm fields={metaAdInfoFields(ad, adSet)} />
              {/* Two columns: the editable copy on the left, the creative on the right — headers match
                  the info form's bold field labels (#6177). */}
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <h3 className="mb-2 font-semibold text-slate-800">Ad Copy</h3>
                  {/* Draft state lives in the shared copyEditor above, whose resync effect keys on
                      ad.id — navigating between ads (even with identical copy) resets it (#6163 P0-5). */}
                  <MetaAdCard ad={ad} metric={metric} currencySymbol={currencySymbol} hideVisual editor={copyEditor} />
                </div>
                <div>
                  <h3 className="mb-2 font-semibold text-slate-800">Visuals</h3>
                  <MetaAdVisual assetId={ad.creative_asset_id} alt={ad.name} />
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
      {showPreview && ad && <MetaAdPreviewModal adId={ad.id} adName={ad.name} onClose={() => setShowPreview(false)} />}
      {UnsavedChangesModal}
      <ConformationModal
        isOpen={showDeleteAdConfirm}
        onClose={() => setShowDeleteAdConfirm(false)}
        onConfirm={handleConfirmDeleteAd}
        title="Delete Ad"
        description={`Are you sure you want to delete "${ad?.name ?? 'this ad'}"? It stops delivering and leaves the campaign; its history stays in reports.`}
        confirmText="Delete Ad"
        cancelText="Cancel"
        isLoading={deleteAdMutation.isPending}
      />
    </div>
  );
}
